import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, Mail, MessageCircle, Search, ChevronDown, ChevronUp, BookOpen, Video, FileText, Send, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import emailjs from '@emailjs/browser';
import './Support.css';

export default function Support() {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium'
  });
  const [formStatus, setFormStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const faqItems = [
    {
      id: 1,
      question: "¿Cómo configuro mis alertas de precio?",
      answer: "Ve a la pestaña 'Monitoreo' en tu dashboard, selecciona un partido y configura el umbral de precio deseado. También puedes ajustar la frecuencia de monitoreo según tu plan."
    },
    {
      id: 2,
      question: "¿Por qué no recibo alertas por correo?",
      answer: "Verifica que tu correo esté correctamente registrado y que no esté en la carpeta de spam. También asegúrate de que las alertas estén activadas en tu configuración."
    },
    {
      id: 3,
      question: "¿Puedo cambiar mi plan de suscripción?",
      answer: "Sí, puedes actualizar tu plan en cualquier momento desde tu panel de usuario."
    },
    {
      id: 4,
      question: "¿Cómo cancelo mi suscripción?",
      answer: "Puedes cancelar tu suscripción desde tu panel de usuario en la sección 'Configuración de Cuenta'. La cancelación será efectiva al final del período actual."
    },
    {
      id: 5,
      question: "¿Los datos de precios son en tiempo real?",
      answer: "Sí, monitoreamos los precios en tiempo real y enviamos alertas tan pronto como se detectan cambios que cumplan con tus criterios configurados."
    },
    {
      id: 6,
      question: "¿Qué pasa si no puedo acceder a mi cuenta?",
      answer: "Utiliza la función '¿Olvidaste tu contraseña?' en la página de login, o contacta a nuestro soporte técnico para asistencia adicional."
    }
  ];

  const supportCategories = [
    {
      title: "Configuración Inicial",
      icon: <BookOpen size={24} />,
      description: "Aprende a configurar tu cuenta y primeras alertas",
      articles: ["Crear tu primera alerta", "Configurar notificaciones", "Elegir tu plan"]
    },
    {
      title: "Gestión de Alertas",
      icon: <MessageCircle size={24} />,
      description: "Optimiza tus alertas y configuraciones",
      articles: ["Editar alertas existentes", "Configurar umbrales", "Frecuencia de monitoreo"]
    },
    {
      title: "Facturación y Pagos",
      icon: <FileText size={24} />,
      description: "Información sobre suscripciones y pagos",
      articles: ["Cambiar plan", "Métodos de pago", "Cancelar suscripción"]
    },
    {
      title: "Solución de Problemas",
      icon: <HelpCircle size={24} />,
      description: "Resuelve problemas comunes",
      articles: ["No recibo alertas", "Error de login", "Problemas de rendimiento"]
    }
  ];

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus(null);

    try {
      // Configuración de EmailJS
      const serviceId = 'service_i95bbnq';
      const templateId = 'template_wfo9ill';
      const publicKey = 'rOyEYxUhY6fBcsWZM';

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        priority: formData.priority,
        to_email: 'blueecodelabs@gmail.com'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        priority: 'medium'
      });
      
      // Ocultar formulario después de 3 segundos
      setTimeout(() => {
        setShowContactForm(false);
        setFormStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredFaq = faqItems.filter(item => 
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="support-page">
      <div className="support-container">
        {/* Header */}
        <div className="support-header">
          <Link to="/login" className="back-button">
            <ArrowLeft size={20} />
            Volver
          </Link>
          <div className="support-title-section">
            <div className="support-icon">
              <HelpCircle size={32} />
            </div>
            <div>
              <h1 className="support-title">Centro de Ayuda</h1>
              <p className="support-subtitle">Encuentra respuestas rápidas y soporte personalizado</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="search-section">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar en preguntas frecuentes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h2 className="section-title">Acciones Rápidas</h2>
          <div className="action-cards">
            <button 
              onClick={() => setShowContactForm(true)} 
              className="action-card"
            >
              <Mail size={24} />
              <h3>Contactar Soporte</h3>
              <p>Envíanos un mensaje directo</p>
            </button>
            <a href="mailto:blueecodelabs@gmail.com" className="action-card">
              <Mail size={24} />
              <h3>Email Directo</h3>
              <p>blueecodelabs@gmail.com</p>
            </a>
            <Link to="/how-it-works" className="action-card">
              <Video size={24} />
              <h3>Ver Tutorial</h3>
              <p>Guía paso a paso</p>
            </Link>
          </div>
        </div>

        {/* Support Categories */}
        <div className="support-categories">
          <h2 className="section-title">Categorías de Ayuda</h2>
          <div className="categories-grid">
            {supportCategories.map((category, index) => (
              <div key={index} className="category-card">
                <div className="category-icon">
                  {category.icon}
                </div>
                <h3 className="category-title">{category.title}</h3>
                <p className="category-description">{category.description}</p>
                <ul className="category-articles">
                  {category.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>{article}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h2 className="section-title">Preguntas Frecuentes</h2>
          <div className="faq-list">
            {filteredFaq.map((item) => (
              <div key={item.id} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(item.id)}
                >
                  <span>{item.question}</span>
                  {expandedFaq === item.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {expandedFaq === item.id && (
                  <div className="faq-answer">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="contact-section">
          <h2 className="section-title">¿Necesitas Ayuda Personalizada?</h2>
          <div className="contact-grid">
            <div className="contact-card">
              <Mail size={24} />
              <h3>Correo Electrónico</h3>
              <p>blueecodelabs@gmail.com</p>
              <p className="contact-note">Respuesta en 24 horas</p>
            </div>
            <div className="contact-card">
              <MessageCircle size={24} />
              <h3>Formulario de Soporte</h3>
              <p>Envíanos tu consulta</p>
              <p className="contact-note">Respuesta rápida</p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="support-footer">
          <Link to="/terms" className="support-link">
            <FileText size={16} />
            Términos y Condiciones
          </Link>
          <Link to="/privacy" className="support-link">
            <Shield size={16} />
            Política de Privacidad
          </Link>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="contact-form-overlay">
          <div className="contact-form-modal">
            <div className="form-header">
              <h3>Contactar Soporte</h3>
              <button 
                onClick={() => setShowContactForm(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            
            {formStatus === 'success' && (
              <div className="form-status success">
                <CheckCircle size={20} />
                <span>¡Mensaje enviado correctamente! Te responderemos pronto.</span>
              </div>
            )}
            
            {formStatus === 'error' && (
              <div className="form-status error">
                <AlertCircle size={20} />
                <span>Error al enviar el mensaje. Inténtalo de nuevo.</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nombre *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Asunto *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Describe brevemente tu problema"
                />
              </div>

              <div className="form-group">
                <label htmlFor="priority">Prioridad</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Mensaje *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="form-textarea"
                  placeholder="Describe detalladamente el problema que estás experimentando..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="cancel-button"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Enviar Mensaje
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
